<!-- ****************************************************
*     		               NOTE                      	*
* At the moment this componente was implemented,    	*
* We used it as a Base for the following components:	*
* accounts, cards, cardsWithSearch, categories,			*
* currency, generic										*
* If you need to change deeply this component in   		*
* the future to work for a new component, STOP			*
*                                                       *
* DO NOT MAKE THIS FILE MORE COMPLEX THAN IT ALREADY IS *
***************************************************** -->
<template>
	<div ref="dropdownWrapper" class="dropdown-wrapper" :class="headerClass">
		<!-- Dropdown label -->
		<label-generic
		v-if="params.labelText"
		:text="params.labelText"
		:mandatory="params.mandatory"
		:errorState="params.errorState"
        :primaryColor="dropdownIsOpen"/>
		<!-- Dropdown -->
		<div class="dropdown-content" :class="{'has-label': params.labelText}" v-click-outside="triggerDropdownClose">
			<!-- Header block -->
			<div class="header-wrapper" ref="inputWrapper" @click="emit('toggleDropdown')">
				<!-- Header slot -->
				<slot name="header"></slot>
				<div
				v-if="!ocultDropArrowIfOnlyOneOption"
				class="open-dropdown-icon-wrapper"
				:class="{'open': dropdownIsOpen }" >
					<!-- Arrow Icon -->
					<arrow-down-icon
					class="arrow-icon"
					:class="{ 'open' : dropdownIsOpen }"/>
				</div>
			</div>
			<!-- *********************************************
			*                 	  Normal Body              	 *
			********************************************** -->
			<teleport :to="`#teleport-dropdown-target${params.tableIdentifier ? `-${params.tableIdentifier}` : ''}`" :disabled="!params.applyTableTeleport">
				<transition name="opening-drawer" @before-enter="beforeEnterHandler">
					<div
					v-if="dropdownIsOpen && !ocultDropArrowIfOnlyOneOption && !mobileDrawerBody"
					class="dropdown-animation-wrapper"
					:style="applyPositionFixedStyles">
						<!-- Search -->
						<div v-if="params.withSearch" class="search-wrapper">
							<input-search
							:params="params.searchParams || {}"
							:inputValue="searchValue"
							@input="searchHandler"/>
						</div>
						<!-- Body -->
						<div class="options-body-wrapper">
							<slot name="body"></slot>
						</div>
						<!-- Empty State -->
						<div v-if="params.withSearch && !filteredOptions.length" class="empty-state">
							{{t('emptyStates.noResults')}}
						</div>
					</div>
				</transition>
			</teleport>
			<!-- *********************************************
			*                 	Mobile Draw Body             *
			********************************************** -->
			<teleport v-if="dropdownIsOpen && !ocultDropArrowIfOnlyOneOption && mobileDrawerBody" to='#more-options-mobile-layer-overlay-teleport'>
				<transition name="opening-drawer">
					<div
					v-if="dropdownIsOpen && !ocultDropArrowIfOnlyOneOption && mobileDrawerBody"
					class="dropdown-mobile-drawer-background-layer"
					@click="emit('closeDropdown')">
						<div class="dropdown-animation-wrapper-mobile-drawer">
							<div v-if="params.mobileDrawerTitle" class="drawer-body-title">{{params.mobileDrawerTitle}}</div>
							<!-- Search -->
							<div v-if="params.withSearch" class="search-wrapper">
								<input-search
								:params="params.searchParams"
								:inputValue="searchValue"
								@input="searchHandler"/>
							</div>
							<!-- Body -->
							<div class="options-body-wrapper">
								<slot name="body"></slot>
							</div>
							<!-- Empty State -->
							<div v-if="params.withSearch && !filteredOptions.length" class="empty-state">
								{{t('emptyStates.noResults')}}
							</div>
						</div>
					</div>
				</transition>
			</teleport>
		</div>
		<!-- Bottom elements -->
		<div v-if="params.errorMessage || params.bottomMessage" class="bottom-elements-container">
			<div class='bottom-message' :class="{'error-state': params.errorState}">{{params.errorState ? params.errorMessage : params.bottomMessage}}</div>
		</div>
	</div>
</template>

<script setup>
/* Vue */
import { ref, computed } from 'vue'
/* i18n */
import { useI18n } from 'vue-i18n'
/* Components */
import LabelGeneric from '@/components/generic/labels/Generic'
import ArrowDownIcon from '@/assets/icons/arrow-down.svg?inline'
import MoreIcon from '@/assets/icons/more.svg?inline'
import InputSearch from '@/components/generic/inputs/Search'
/* Assets */
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
const { getMobileAppState, getFooterState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['closeDropdown', 'toggleDropdown', 'search'])

/** props received
@mobileDrawerBody {Boolean} prop to apply bottom mobile drawer style body
@params { Object } with:
	- labelText { String}
	- mandatory { Boolean }
	- errorState { Boolean }
	- errorMessage { String }
	- bottomMessage { String }
	- applyTableTeleport {Boolean} - prop to display dropdown body in a teleport in table
	- withSearch { Boolean }
	- searchParams { Object } with
	- placeholder { String }
@ocultDropArrowIfOnlyOneOption {Boolean} - prop to hide arrow
@options { Array }
@dropdownType { String }
@dropdownIsOpen { String }
@headerClass { String } - possible classes: medium(height is 56), big(height is 64), when none is provided we have the standard height of 64
*/
const props = defineProps({
	mobileDrawerBody: Boolean,
	params: Object,
	ocultDropArrowIfOnlyOneOption: Boolean,
	options: Array,
	dropdownType: String,
	dropdownIsOpen: Boolean,
	headerClass: String
})

const { t } = useI18n()
const dropdownWrapper = ref('')
const inputWrapper = ref('')

/*************************************************
*                                                *
*                   Search Logic                 *
*                                                *
*************************************************/
const searchValue = ref('')

const filteredOptions = computed(() => {
	if (!props.params.withSearch || !searchValue.value.length) return props.options

	return props.options.filter(option => {
		const listOfWords = searchValue.value.split(' ')

		let hasWord

		listOfWords.forEach(word => {
			const parsedWord = word.toLowerCase()
			let matching
			let description

			switch (props.dropdownType) {
				case 'account':
					description = option.description ? ignoreAccents(option.description).toLowerCase() : ''

					matching = option.accountNumber.replace(/ /g, '').includes(parsedWord) || description.includes(ignoreAccents(parsedWord))
					break
				case 'card':
					description = option.cardDescription ? ignoreAccents(option.cardDescription).toLowerCase() : ''

					matching = option.cardNumber.replace(/ /g, '').includes(parsedWord) || description.includes(ignoreAccents(parsedWord))
					break
				case 'currency':
					description = option.description ? ignoreAccents(option.description).toLowerCase() : ''

					matching = option.currency.toLowerCase().includes(parsedWord) || description.includes(ignoreAccents(parsedWord))
					break
				default:
					matching = ignoreAccents(option.text).toLowerCase().includes(ignoreAccents(parsedWord))
					break
			}

			if (matching) hasWord = true
		})

		return hasWord
	})
})

/* To ignore accents on search */
const ignoreAccents = (string) => {
	return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

const searchHandler = event => {
	searchValue.value = event
	emit('search', filteredOptions.value)
}

const clearSearchValue = () => {
	searchValue.value = ''
}

// eslint-disable-next-line no-undef
defineExpose({ clearSearchValue }) // We need the comment above because eslint can't deal with undefined macros yet

/*************************************************
*                                                *
*                Open/Close Logic                *
*                                                *
*************************************************/
const applyPositionFixedStyles = ref(null)
const APP_HEADER_HEIGHT_FOR_CALC = 208
const APP_SIDE_MODAL_HEADER_HEIGHT_FOR_CALC = 200

/**
* Here we check if the distance to the bottom is bigger than the dropdown size
*/
const beforeEnterHandler = (el) => {
	let distanceToBottom
	let distanceToLeft
	let distanceToRight

	const valueToBreathe = 20 // spacing between dropdown and bottom plus dropdown and input
	const maxHeight = 288 // same value we have @css
	const minHeightAllowed = 140 // based on biggest row height, allowing to see at least two of there
	const footerHeight = getFooterState.value ? (getMobileAppState.value ? 50 : 95) : 0

	if (props.params?.applyTableTeleport) {
		const dropdownWrapperStyles = dropdownWrapper.value.getBoundingClientRect()
		const cellWrapper = validateIfParentExists(dropdownWrapper.value, 'dropdown-cell-wrapper')
		const rowWrapper = validateIfParentExists(dropdownWrapper.value, 'data-table-row')
		const tableWrapper = validateIfParentExists(dropdownWrapper.value, 'wrapping')

		applyPositionFixedStyles.value = {
			left: `${cellWrapper.offsetLeft + 8}px`, // 8px on padding on left side
			top: `${rowWrapper.offsetTop + 20}px`, // dropdown-cell padding top + space between body and dropdown top
			width: `${dropdownWrapperStyles.width - 16}px`, // 8px on each side
			'z-index': 10
		}

		distanceToBottom = window.innerHeight - tableWrapper.getBoundingClientRect().top - rowWrapper.offsetTop - rowWrapper.clientHeight
	} else {
		/* We are using header top to calc distanceToBottom, because our body starts on top of our header */
		distanceToBottom = window.innerHeight - inputWrapper.value.getBoundingClientRect().top - inputWrapper.value.clientHeight - valueToBreathe - footerHeight
		/* Check If inside router-content-wrapper and with menu at right */
		if (!getMobileAppState.value) {
			if (validateIfParentExists(inputWrapper.value, 'router-content-wrapper')) {
				distanceToLeft = inputWrapper.value.getBoundingClientRect().left
				if (validateIfParentExists(inputWrapper.value, 'has-side-menu')) distanceToLeft = distanceToLeft - 260 /* Menu width */
			}

			distanceToRight = window.innerWidth - (dropdownWrapper.value.getBoundingClientRect().left + dropdownWrapper.value.offsetWidth)
		}
	}

	const insideSideModal = validateIfParentExists(dropdownWrapper.value, 'modal-content-wrapper')

	if (distanceToLeft <= 75 && !insideSideModal) {
		el.style.left = '8px'
		el.style.right = 'unset'
	}

	if (distanceToRight <= 24 && !insideSideModal) {
		el.style.right = 0
		el.style.left = 'unset'
	}

	/* If we have space for our dropdown nothing is need */
	if (distanceToBottom > maxHeight) return

	/**
	* If distance to Bottom is less than our maxHeight for dropdown body,
	* and bigger than an acceptable min we will change our maxHeight
	*/
	if (distanceToBottom < maxHeight && distanceToBottom > minHeightAllowed) {
		el.style.maxHeight = `${distanceToBottom}px`
		return
	}

	/**
	* If we are fliping our dropdown, and are inside a table, we need to check distance to above
	* if distance is less than our maxHeight for dropdown body and bigger than an acceptable min we will change our maxHeight
	*/
	if (props.params?.applyTableTeleport) {
		applyPositionFixedStyles.value = {
			...applyPositionFixedStyles.value,
			top: `calc(${applyPositionFixedStyles.value.top} - ${minHeightAllowed}px)`
		}
		return
	}

	if (validateIfParentExists(inputWrapper.value, 'feature-content-container')) {
        const distanceToTop = inputWrapper.value.getBoundingClientRect().bottom - APP_HEADER_HEIGHT_FOR_CALC

        if (distanceToTop < maxHeight) {
			el.style.maxHeight = `${distanceToTop}px`
		}
    } else if (insideSideModal) {
		const distanceToTop = inputWrapper.value.getBoundingClientRect().bottom - APP_SIDE_MODAL_HEADER_HEIGHT_FOR_CALC

        if (distanceToTop < maxHeight) {
			el.style.maxHeight = `${distanceToTop}px`
		}
	}

	/** Fliping dropdown up */
	el.style.top = 'unset'
	el.style.bottom = '10px'
}

const triggerDropdownClose = $event => {
	if (!props.dropdownIsOpen ||
		validateIfParentExists($event.target, 'dropdown-mobile-drawer-background-layer') ||
		$event.target.className === 'dropdown-mobile-drawer-background-layer') return

	clearSearchValue()

	emit('closeDropdown')
}
</script>

<style lang="scss" scoped>
.dropdown-wrapper {
	// width: 512px;
	// max-width: 100%;
	width: 100%;

	display: flex;
	flex-direction: column;
    gap: 8px;

	position: relative;

	&.big {
		.dropdown-content {
			.header-wrapper{
				height: 62px;
			}
		}
	}

	&.medium {
		.dropdown-content {
			.header-wrapper{
				height: 56px;
			}
		}
	}

	.dropdown-content {
		width: 100%;

		position: relative;

		.header-wrapper {
			width: 100%;
			height: 40px;

			.open-dropdown-icon-wrapper {
				position: absolute;
                top: 0;
                bottom: 0;
				right: 16px;

                display: flex;
                align-items: center;

				cursor: pointer;
				pointer-events: none;

				&.open {
					pointer-events: all;
				}

				svg.arrow-icon {
                    width: 16px;
                    height: 16px;

					pointer-events: none;
					transition: all .4s ease;

					:deep(.change-color-fill) {
						fill: var(--secondary-dark-2);
					}

					&.open {
						transform: rotate(180deg);
					}
				}
			}
		}

		.opening-enter-active, .opening-leave-active{
			opacity: 1;
			transform: translateZ(0);
		}

		.opening-enter-from, .opening-leave-to{
			opacity: 0;
			transform: translateZ(-1400px);
		}
	}

	.bottom-elements-container {
		display: flex;
		justify-content: space-between;

		width: 100%;
		min-height: 12px;
		margin-top: 1px;

		.bottom-message {
            @include H7-Regular;
			line-height: 16px;
			text-align: left;
			color: var(--secondary-dark-1);

			&.error-state {
				color: var(--negative);
			}
		}
	}
}

.dropdown-animation-wrapper {
	width: calc(100% - 16px);
	max-height: 288px;

	display: flex;
	flex-direction: column;

	padding: 8px 0;

	opacity: 1;
	transform: translateZ(0);
	transition: all .2s cubic-bezier(0.250, 0.460, 0.450, 0.940);

	position: absolute;
	top: 8px;
	left: 8px;
	z-index: 20;

	background-color: var(--secondary-light-4);
	box-shadow: var(--box-shadow-2);
	overflow-x: hidden;
	border-radius: 8px;

	&.fixed-position {
		position: fixed;
	}

	&.fixed-position-mobile-drawer {
		position: fixed;
		top: unset;
		bottom: 0;

		border-radius: 16px 16px 0px 0px;
		box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.15);

		transition: transform 0.3s linear;
        transform: translateY(0);

		.drawer-body-title {
			width: 100%;
			padding: 16px;

			@include H5-Medium;
			color: var(--secondary-dark-3);
		}

        &.opening-drawer-enter-active,
        &.opening-drawer-leave-active {
            transform: translateY(0);
        }

        &.opening-drawer-enter-from,
        &.opening-drawer-leave-to {
            transform: translateY(110%);
        }
	}

	.search-wrapper {
		padding: 8px;
		padding-top: 0;
	}

	.options-body-wrapper {
		width: 100%;
		height: auto;

		@include generic-scrollbar(var(--secondary-light-4), var(--primary));
	}

	.empty-state {
		padding: 0 16px;

		@include H7-Regular;
		line-height: 16px;

		color: var(--secondary-dark-1);
	}
}

.dropdown-mobile-drawer-background-layer {
	@include moreOptionsMobilelayerOverlayTeleportStyles;
	flex-direction: column;
	opacity: 1;
	transition: opacity 0.1s linear;

	.dropdown-animation-wrapper-mobile-drawer {
		width: 100%;
		padding: 16px 0;

		display: flex;
		flex-direction: column;

		box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);
		background-color: var(--secondary-light-4);
		border-radius: 16px 16px 0px 0px;
		transition: all 0.3s linear;

		.drawer-body-title {
			width: 100%;
			padding: 0 16px;
			margin-bottom: 12px;

			@include H5-Medium;
			color: var(--secondary-dark-3);
		}
	}

	&.opening-drawer-enter-active,
	&.opening-drawer-leave-active {
		opacity: 1;
		.dropdown-animation-wrapper-mobile-drawer {
			transform: translateY(0);
		}
	}

	&.opening-drawer-enter-from,
	&.opening-drawer-leave-to {
		opacity: 0;
		.dropdown-animation-wrapper-mobile-drawer {
			transform: translateY(110%);
		}
	}

}
</style>
